import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  fetchPortfolioOverview,
  PortfolioOverviewResponse,
  PortfolioTenantSummary,
} from "@/api/portfolioApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

function roleWorkspacePath(role: string, tenantSlug: string): string {
  const map: Record<string, string> = {
    ddo: `/${tenantSlug}/ddo/dashboard`,
    pm: `/${tenantSlug}/pm/dashboard`,
    pc: `/${tenantSlug}/pc/dashboard`,
    finance: `/${tenantSlug}/finance/dashboard`,
    management: `/${tenantSlug}/management/dashboard`,
    client: `/${tenantSlug}/client/dashboard`,
    super_admin: `/${tenantSlug}/admin/tenant-settings`,
  };
  return map[role] || `/${tenantSlug}/login`;
}

function selectTenantWorkspace(
  tenant: PortfolioTenantSummary,
  role: string,
  navigate: ReturnType<typeof useNavigate>,
  filters: {
    dateFrom: string;
    dateTo: string;
    programCode: string;
    academicYear: string;
    applicantName: string;
  }
) {
  sessionStorage.setItem("tenant_id", String(tenant.tenant_id));
  sessionStorage.setItem("tenant_slug", tenant.tenant_slug);
  sessionStorage.setItem("operations_date_from", filters.dateFrom);
  sessionStorage.setItem("operations_date_to", filters.dateTo);
  sessionStorage.setItem("operations_program_code", filters.programCode);
  sessionStorage.setItem("operations_academic_year", filters.academicYear);
  sessionStorage.setItem("operations_applicant_name", filters.applicantName);
  navigate(roleWorkspacePath(role, tenant.tenant_slug));
}

const Stat = ({
  label,
  value,
  tone = "text-gray-950",
}: {
  label: string;
  value: number;
  tone?: string;
}) => (
  <Card>
    <CardHeader>
      <CardTitle className="text-sm text-muted-foreground">{label}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
    </CardContent>
  </Card>
);

const OperationsOverview: React.FC = () => {
  const [overview, setOverview] = useState<PortfolioOverviewResponse | null>(null);
  const [error, setError] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [programCode, setProgramCode] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [applicantName, setApplicantName] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    setError("");
    try {
      setOverview(
        await fetchPortfolioOverview({
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          program_code: programCode.trim() || undefined,
          academic_year: academicYear.trim() ? Number(academicYear.trim()) : undefined,
          applicant_name: applicantName.trim() || undefined,
        })
      );
    } catch (err) {
      console.error(err);
      setError("Could not load all-tenant operations overview.");
    }
  };

  useEffect(() => {
    sessionStorage.removeItem("tenant_id");
    sessionStorage.removeItem("tenant_slug");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedTenants = useMemo(() => {
    const isDdo = overview?.role === "ddo";
    return [...(overview?.tenants ?? [])].sort((a, b) => {
      const riskA =
        (isDdo ? 0 : a.routing_failures + a.unassigned_open_submissions) +
        a.stale_assignments +
        a.locked_submissions;
      const riskB =
        (isDdo ? 0 : b.routing_failures + b.unassigned_open_submissions) +
        b.stale_assignments +
        b.locked_submissions;
      return riskB - riskA || b.pending_submissions - a.pending_submissions;
    });
  }, [overview]);

  const isDdoOverview = overview?.role === "ddo";

  return (
    <div className="w-full max-w-[1400px] space-y-6">
      <div className="rounded-md border border-blue-200 bg-blue-50 p-4">
        <div className="text-sm font-semibold text-blue-950">
          Cross-tenant supervisory context
        </div>
        <div className="mt-1 text-sm text-blue-900">
          This view is read-only. Choose a tenant workspace before taking any
          workflow, assignment, bulk, or timeline action.
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">All-Up Operations</h1>
          <p className="text-sm text-muted-foreground">
            Aggregated operational visibility across assigned tenants.
          </p>
        </div>
        <Button variant="outline" onClick={load}>
          Refresh
        </Button>
      </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        {!overview && !error ? (
          <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
            {[...Array(6)].map((_, index) => (
              <Skeleton key={index} className="h-28 w-full" />
            ))}
          </div>
        ) : null}

        {overview ? (
          <>
            <div className="grid gap-4 md:grid-cols-2 2xl:grid-cols-3">
              <Stat label="Tenants" value={overview.tenant_count} />
              <Stat
                label={isDdoOverview ? "My Total Applications" : "Active assignments"}
                value={overview.totals.active_assignments ?? 0}
              />
              {!isDdoOverview ? (
                <Stat
                  label="Unassigned open"
                  value={overview.totals.unassigned_open_submissions}
                  tone={
                    overview.totals.unassigned_open_submissions > 0
                      ? "text-red-600"
                      : "text-gray-950"
                  }
                />
              ) : null}
              <Stat
                label="Stale assignments"
                value={overview.totals.stale_assignments}
                tone={overview.totals.stale_assignments > 0 ? "text-amber-700" : "text-gray-950"}
              />
              {!isDdoOverview ? (
                <Stat
                  label="Assignment routing failures"
                  value={overview.totals.routing_failures}
                  tone={overview.totals.routing_failures > 0 ? "text-red-600" : "text-gray-950"}
                />
              ) : null}
              <Stat
                label={isDdoOverview ? "Locked Applications" : "Locked submissions"}
                value={overview.totals.locked_submissions}
                tone={overview.totals.locked_submissions > 0 ? "text-red-600" : "text-gray-950"}
              />
              {isDdoOverview ? (
                <Stat
                  label="Total Applications Disbursed"
                  value={overview.totals.total_applications_disbursed ?? 0}
                />
              ) : null}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Filters</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[repeat(5,minmax(0,1fr))_auto] 2xl:items-end">
                <label className="flex flex-col text-sm">
                  <span className="font-medium text-muted-foreground">From</span>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(event) => setDateFrom(event.target.value)}
                    className="rounded border px-3 py-2"
                  />
                </label>
                <label className="flex flex-col text-sm">
                  <span className="font-medium text-muted-foreground">To</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(event) => setDateTo(event.target.value)}
                    className="rounded border px-3 py-2"
                  />
                </label>
                <Input
                  value={programCode}
                  onChange={(event) => setProgramCode(event.target.value)}
                  placeholder="Program code"
                />
                <Input
                  value={academicYear}
                  onChange={(event) => setAcademicYear(event.target.value)}
                  placeholder="Academic year"
                  inputMode="numeric"
                />
                <Input
                  value={applicantName}
                  onChange={(event) => setApplicantName(event.target.value)}
                  placeholder="Applicant name"
                />
                <Button onClick={load}>Apply</Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Tenant Workspaces</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {sortedTenants.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No active tenant memberships found.
                  </p>
                ) : null}
                {sortedTenants.map((tenant) => (
                  <div
                    key={tenant.tenant_id}
                    className={`grid gap-3 rounded-md border bg-white p-4 md:items-center ${
                      isDdoOverview
                        ? "md:grid-cols-[1.5fr_repeat(4,0.8fr)_auto]"
                        : "md:grid-cols-[1.5fr_repeat(6,0.7fr)_auto]"
                    }`}
                  >
                    <div>
                      <div className="font-semibold">{tenant.tenant_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {tenant.tenant_slug}
                      </div>
                    </div>
                    <div className="text-sm">
                      {isDdoOverview ? "Total Applications" : "Active assignments"}{" "}
                      {tenant.active_assignments}
                    </div>
                    {!isDdoOverview ? (
                      <div className="text-sm">
                        Unassigned open {tenant.unassigned_open_submissions}
                      </div>
                    ) : null}
                    <div className="text-sm">
                      Stale assignments {tenant.stale_assignments}
                    </div>
                    {!isDdoOverview ? (
                      <div className="text-sm">
                        Routing failures {tenant.routing_failures}
                      </div>
                    ) : null}
                    <div className="text-sm">
                      {isDdoOverview ? "Locked Applications" : "Locked submissions"}{" "}
                      {tenant.locked_submissions}
                    </div>
                    {isDdoOverview ? (
                      <div className="text-sm">
                        Total Applications Disbursed{" "}
                        {tenant.total_applications_disbursed ?? 0}
                      </div>
                    ) : null}
                    {!isDdoOverview ? (
                      <div className="text-sm">
                        Open submissions {tenant.pending_submissions}
                      </div>
                    ) : null}
                    {!isDdoOverview ? (
                      <div className="text-sm">
                        Total Applications Disbursed{" "}
                        {tenant.total_applications_disbursed ?? 0}
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      onClick={() =>
                        selectTenantWorkspace(tenant, overview.role, navigate, {
                          dateFrom,
                          dateTo,
                          programCode,
                          academicYear,
                          applicantName,
                        })
                      }
                    >
                      Enter Tenant
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        ) : null}
    </div>
  );
};

export default OperationsOverview;
